// src/utils/businessHours.js

/**
 * Business hours utility functions
 * Handles checking if restaurant is open, calculating next opening time, etc.
 */

const DAY_NAMES = {
  en: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  es: [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ],
};

const TIME_WORDS = {
  en: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    at: 'at',
  },
  es: {
    today: 'Hoy',
    tomorrow: 'Mañana',
    at: 'a las',
  },
};

/**
 * Get the effective override state, considering expiration
 * @param {string|null} override - Raw override value ('open' | 'closed' | null)
 * @param {number|null} expiration - Expiration timestamp (null = indefinite)
 * @returns {string|null} Effective override ('open' | 'closed' | null)
 */
export const getEffectiveOverride = (override, expiration) => {
  // No override set
  if (!override) return null;

  // Indefinite override (no expiration)
  if (!expiration) return override;

  // Check if expired
  const now = Date.now();
  if (now >= expiration) {
    return null; // Expired, treat as no override
  }

  // Still active
  return override;
};

/**
 * How long a customer who was already in checkout may keep going after the
 * owner force-closes the restaurant.
 */
export const FORCE_CLOSE_GRACE_MINUTES = 5;

/**
 * Is `now` inside the grace window that follows a force close? Measured from
 * the moment the override was set (`siteInfo/overrideSetAt`), never from a
 * visitor's page load: a close that has been in place for days must not look
 * like one that is about to happen (#668). A close with no recorded time has
 * no grace.
 *
 * @param {number|null} overrideSetAt - Epoch ms the override was written
 * @param {number} [now] - Epoch ms
 * @param {number} [graceMinutes]
 * @returns {boolean}
 */
export const isWithinForceCloseGrace = (
  overrideSetAt,
  now = Date.now(),
  graceMinutes = FORCE_CLOSE_GRACE_MINUTES,
) => {
  if (typeof overrideSetAt !== 'number' || !Number.isFinite(overrideSetAt)) {
    return false;
  }
  return now >= overrideSetAt && now < overrideSetAt + graceMinutes * 60000;
};

/**
 * Convert HHMM integer to readable time string
 * @param {number} hhmm - Time in HHMM format (e.g., 1430 = 2:30 PM)
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export const formatTimeFromHHMM = (hhmm) => {
  const hours = Math.floor(hhmm / 100);
  const minutes = hhmm % 100;

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, '0');

  return `${displayHours}:${displayMinutes} ${period}`;
};

/**
 * Convert Date object to HHMM integer
 * @param {Date} date - JavaScript Date object
 * @returns {number} Time in HHMM format
 */
const dateToHHMM = (date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return hours * 100 + minutes;
};

/**
 * Check if current time is within a time period
 * @param {number} currentHHMM - Current time in HHMM format
 * @param {Object} period - Time period {start, end}
 * @param {number} gracePeriodMinutes - Grace period in minutes (stops orders BEFORE closing)
 * @returns {boolean}
 */
const isWithinPeriod = (currentHHMM, period, gracePeriodMinutes = 0) => {
  const { start, end } = period;

  // Calculate adjusted end time with grace period
  // Grace period REDUCES closing time (stop orders earlier)
  const endHours = Math.floor(end / 100);
  const endMinutes = end % 100;
  const totalEndMinutes = endHours * 60 + endMinutes - gracePeriodMinutes;
  const adjustedEndHours = Math.floor(totalEndMinutes / 60);
  const adjustedEndMinutes = totalEndMinutes % 60;
  const adjustedEnd = adjustedEndHours * 100 + adjustedEndMinutes;

  return currentHHMM >= start && currentHHMM <= adjustedEnd;
};

/**
 * Convert current time to restaurant's timezone
 * @param {Date} date - Date to convert
 * @param {string} timezone - IANA timezone (e.g., 'America/Los_Angeles')
 * @returns {Date} Date object representing the time in the restaurant's timezone
 */
const convertToRestaurantTime = (date, timezone) => {
  if (!timezone) return date;

  // Get time string in restaurant's timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const getPart = (type) => parts.find((p) => p.type === type)?.value;

  // Create a new Date in restaurant timezone
  return new Date(
    `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}:${getPart('second')}`,
  );
};

/**
 * Check if restaurant is currently open
 * @param {Object} hours - Business hours by day (0-6)
 * @param {string|null} override - Manual override ('open' | 'closed' | null)
 * @param {Date} currentTime - Current time to check (in user's timezone)
 * @param {number} gracePeriodMinutes - Minutes before closing to stop accepting orders
 * @param {string} timezone - Restaurant's IANA timezone (e.g., 'America/Los_Angeles')
 * @returns {boolean} True if open, false if closed
 */
export const isCurrentlyOpen = (
  hours,
  override,
  currentTime = new Date(),
  gracePeriodMinutes = 0,
  timezone = null,
) => {
  // Check override first
  if (override === 'open') return true;
  if (override === 'closed') return false;

  if (!hours) return false;

  // Convert to restaurant's timezone
  const restaurantTime = timezone
    ? convertToRestaurantTime(currentTime, timezone)
    : currentTime;

  const day = restaurantTime.getDay();
  const dayHours = hours[day];
  if (!dayHours || dayHours.length === 0) return false;

  // Convert restaurant time to HHMM format
  const currentHHMM = dateToHHMM(restaurantTime);

  // Check if current time falls within any period
  return dayHours.some((period) =>
    isWithinPeriod(currentHHMM, period, gracePeriodMinutes),
  );
};

/**
 * Check if we're in grace period (restaurant is open but not taking orders)
 * @param {Object} hours - Business hours by day (0-6)
 * @param {string|null} override - Manual override ('open' | 'closed' | null)
 * @param {Date} currentTime - Current time to check
 * @param {number} gracePeriodMinutes - Minutes before closing to stop accepting orders
 * @param {string} timezone - Restaurant's IANA timezone
 * @returns {boolean} True if in grace period, false otherwise
 */
export const isInGracePeriod = (
  hours,
  override,
  currentTime = new Date(),
  gracePeriodMinutes = 0,
  timezone = null,
) => {
  // No grace period if overridden
  if (override === 'open' || override === 'closed') return false;

  if (!hours || gracePeriodMinutes === 0) return false;

  // Convert to restaurant's timezone
  const restaurantTime = timezone
    ? convertToRestaurantTime(currentTime, timezone)
    : currentTime;

  const day = restaurantTime.getDay();
  const dayHours = hours[day];
  if (!dayHours || dayHours.length === 0) return false;

  const currentHHMM = dateToHHMM(restaurantTime);

  // Check if we're open WITHOUT grace period but closed WITH grace period
  const isOpenWithoutGrace = dayHours.some((period) =>
    isWithinPeriod(currentHHMM, period, 0),
  );
  const isOpenWithGrace = dayHours.some((period) =>
    isWithinPeriod(currentHHMM, period, gracePeriodMinutes),
  );

  // In grace period if open without grace but not open with grace
  return isOpenWithoutGrace && !isOpenWithGrace;
};

/**
 * Get next opening time
 * @param {Object} hours - Business hours object (indexed by day 0-6)
 * @param {string|null} override - Admin override ('open' | 'closed' | null)
 * @param {Date} now - Current date/time (defaults to new Date())
 * @param {string} language - Language code ('en' | 'es')
 * @returns {Object|null} Next opening time {day, time, formatted} or null
 */
export const getNextOpeningTime = (
  hours,
  override = null,
  now = new Date(),
  language = 'en',
) => {
  // Get language-specific strings
  const dayNames = DAY_NAMES[language] || DAY_NAMES.en;
  const timeWords = TIME_WORDS[language] || TIME_WORDS.en;
  // If force closed, return null (no opening time available)
  if (override === 'closed') return null;

  // If force open, return null (already open)
  if (override === 'open') return null;

  // Handle invalid hours
  if (!hours || typeof hours !== 'object') return null;

  const currentDay = now.getDay();
  const currentTime = dateToHHMM(now);

  // Check rest of today first
  const todayHours = hours[currentDay];
  if (todayHours && Array.isArray(todayHours)) {
    for (const period of todayHours) {
      if (period.start > currentTime) {
        return {
          day: currentDay,
          time: period.start,
          formatted: `${timeWords.today} ${timeWords.at} ${formatTimeFromHHMM(period.start)}`,
        };
      }
    }
  }

  // Check next 7 days
  for (let i = 1; i <= 7; i++) {
    const checkDay = (currentDay + i) % 7;
    const dayHours = hours[checkDay];

    if (dayHours && Array.isArray(dayHours) && dayHours.length > 0) {
      const firstPeriod = dayHours[0];

      // Use "Tomorrow" only if it's the next calendar day AND doesn't wrap the week
      // Otherwise use the day name for clarity
      let dayName;
      if (i === 1 && checkDay === (currentDay + 1) % 7) {
        // True tomorrow (no week wrap)
        dayName =
          checkDay === currentDay + 1 ? timeWords.tomorrow : dayNames[checkDay];
      } else {
        dayName = dayNames[checkDay];
      }

      return {
        day: checkDay,
        time: firstPeriod.start,
        formatted: `${dayName} ${timeWords.at} ${formatTimeFromHHMM(firstPeriod.start)}`,
      };
    }
  }

  // No opening hours found in next 7 days
  return null;
};

/**
 * Format business hours for display
 * @param {Object} hours - Business hours object (indexed by day 0-6)
 * @returns {Object} Formatted hours by day
 */
export const formatBusinessHours = (hours) => {
  const formatted = {};

  for (let day = 0; day < 7; day++) {
    const dayHours = hours?.[day];

    if (!dayHours || !Array.isArray(dayHours) || dayHours.length === 0) {
      formatted[day] = 'Closed';
    } else {
      formatted[day] = dayHours
        .map(
          (period) =>
            `${formatTimeFromHHMM(period.start)} - ${formatTimeFromHHMM(period.end)}`,
        )
        .join(', ');
    }
  }

  return formatted;
};

/**
 * How many days beyond today a customer may schedule. 1 = today and tomorrow
 * (#485). Deliberately a constant, not an admin setting — growing it later is
 * a code change plus a release, which the owner accepted.
 */
export const ADVANCE_ORDER_DAYS = 1;

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * The service dates a customer may choose, as `YYYY-MM-DD` keys in the
 * restaurant's timezone. Read in the restaurant's zone on purpose: at 22:00
 * in Denver it is already tomorrow in UTC, and "today" must mean the
 * restaurant's today.
 *
 * @param {Date} [now]
 * @param {{timezone?: string|null, advanceDays?: number}} [options]
 * @returns {string[]} e.g. ['2026-09-23', '2026-09-24']
 */
export const serviceDateKeys = (
  now = new Date(),
  { timezone = null, advanceDays = ADVANCE_ORDER_DAYS } = {},
) => {
  const local = timezone
    ? convertToRestaurantTime(now, timezone)
    : new Date(now);
  const keys = [];
  for (let offset = 0; offset <= advanceDays; offset += 1) {
    // Date arithmetic on the calendar date, so month and year roll over.
    const day = new Date(
      local.getFullYear(),
      local.getMonth(),
      local.getDate() + offset,
    );
    keys.push(
      `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}`,
    );
  }
  return keys;
};

/** Offset (ms) between UTC and the restaurant's wall clock at a given instant. */
const zonedOffsetMs = (instantMs, timezone) => {
  if (!timezone) return -new Date(instantMs).getTimezoneOffset() * 60000;
  const local = convertToRestaurantTime(new Date(instantMs), timezone);
  const asIfUtc = Date.UTC(
    local.getFullYear(),
    local.getMonth(),
    local.getDate(),
    local.getHours(),
    local.getMinutes(),
    local.getSeconds(),
  );
  return asIfUtc - Math.floor(instantMs / 1000) * 1000;
};

/**
 * The UTC instant of a wall-clock time on a given date in `timezone`.
 *
 * Resolved twice: the first pass uses the offset at the guessed instant, the
 * second re-reads it at the corrected instant, which is what makes the
 * daylight-saving boundaries land correctly instead of an hour out.
 *
 * @returns {number} epoch ms
 */
export const zonedWallTimeToMs = (
  { year, month, day, hours = 0, minutes = 0, seconds = 0, ms = 0 },
  timezone = null,
) => {
  const guess = Date.UTC(year, month - 1, day, hours, minutes, seconds, ms);
  const firstPass = guess - zonedOffsetMs(guess, timezone);
  return guess - zonedOffsetMs(firstPass, timezone);
};

/**
 * The next instant the published schedule opens, strictly after `now`.
 * During service that is the next period's start (usually tomorrow's
 * opening), which is what "closed until we next open" means to the owner.
 *
 * @param {Object|null} hours - Weekly hours keyed by day 0-6
 * @param {Date} [now]
 * @param {string|null} [timezone] - Restaurant IANA timezone
 * @returns {number|null} epoch ms, or null when no day in the next week opens
 */
export const getNextOpeningMs = (hours, now = new Date(), timezone = null) => {
  if (!hours || typeof hours !== 'object') return null;
  const nowMs = now.getTime();
  const keys = serviceDateKeys(now, { timezone, advanceDays: 7 });

  for (const key of keys) {
    const [year, month, day] = key.split('-').map(Number);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    const periods = hours[weekday];
    if (!Array.isArray(periods)) continue;

    const starts = periods
      .map(({ start }) =>
        zonedWallTimeToMs(
          {
            year,
            month,
            day,
            hours: Math.floor(start / 100),
            minutes: start % 100,
          },
          timezone,
        ),
      )
      .filter((ms) => ms > nowMs)
      .sort((a, b) => a - b);
    if (starts.length > 0) return starts[0];
  }
  return null;
};

/**
 * The last instant a scheduled pickup may land on: end of the final service
 * date, in the restaurant's timezone. Replaces the old flat 24-hour lead,
 * which wrongly rejected "tomorrow at 7 PM" when asked late at night and
 * wrongly allowed "tomorrow at 9 AM" when asked this morning (#485).
 *
 * @returns {number} epoch ms
 */
export const endOfServiceHorizon = (
  now = new Date(),
  { timezone = null, advanceDays = ADVANCE_ORDER_DAYS } = {},
) => {
  const keys = serviceDateKeys(now, { timezone, advanceDays });
  const [year, month, day] = keys[keys.length - 1].split('-').map(Number);
  return zonedWallTimeToMs(
    { year, month, day, hours: 23, minutes: 59, seconds: 59, ms: 999 },
    timezone,
  );
};

/**
 * Wall-clock minutes since midnight for a Date, read in the restaurant's
 * timezone when one is given.
 */
const toRestaurantClock = (date, timezone) => {
  const local = timezone ? convertToRestaurantTime(date, timezone) : date;
  return {
    day: local.getDay(),
    minutes: local.getHours() * 60 + local.getMinutes(),
  };
};

const hhmmToMinutes = (hhmm) => Math.floor(hhmm / 100) * 60 + (hhmm % 100);

/**
 * May a scheduled pickup land at `date`? One rule shared by the checkout slot
 * grid and the createOrder/payOrder functions (#547), so the kitchen can never
 * receive a scheduled ticket for a time the restaurant is not open.
 *
 * A time is bookable when it is in the future, inside the horizon, and:
 * The horizon is the end of the last service date (today + ADVANCE_ORDER_DAYS)
 * in the restaurant's timezone, so tomorrow is bookable all day and nothing
 * beyond it ever is.
 *
 *   - with no override, inside a scheduled period: at or after `start`, no
 *     later than `end - gracePeriodMinutes`, and strictly before `end` (the
 *     closing minute itself is never bookable, even with no grace period);
 *   - with a timed force-open, before `overrideExpiration` less the grace
 *     period — OR inside the regular schedule. The override covers the hours
 *     the owner added; once it lapses the schedule resumes, so an early
 *     force-open still offers the regular late-morning slots, while a late
 *     one never extends into hours the schedule keeps closed;
 *   - with an indefinite force-open, the published schedule applies as
 *     usual: the override adds no bookable window of its own (there is no
 *     end to book against) but does not remove the day's normal hours.
 * An indefinite force-close rejects everything; a timed one rejects only the
 * times inside it (#668).
 *
 * @param {Date|null} date - The requested pickup time
 * @param {Object} policy
 * @param {Object|null} policy.hours - Weekly hours keyed by day 0-6
 * @param {string|null} policy.override - Effective override ('open' | 'closed' | null)
 * @param {number|null} [policy.overrideExpiration] - Epoch ms the override lapses, null = indefinite
 * @param {number} [policy.gracePeriodMinutes]
 * @param {string|null} [policy.timezone] - Restaurant IANA timezone
 * @param {Date} [policy.now] - Injectable for tests
 * @param {number} [policy.advanceDays] - Days beyond today that may be booked
 * @param {number} [policy.maxPickupMs] - Explicit horizon override (tests)
 * @returns {{ok: true} | {ok: false, code: 'PICKUP_INVALID' | 'PICKUP_IN_PAST' | 'PICKUP_TOO_FAR' | 'PICKUP_OUTSIDE_HOURS'}}
 */
export const isPickupTimeBookable = (
  date,
  {
    hours,
    override = null,
    overrideExpiration = null,
    gracePeriodMinutes = 0,
    timezone = null,
    now = new Date(),
    advanceDays = ADVANCE_ORDER_DAYS,
    maxPickupMs = null,
  } = {},
) => {
  if (typeof date?.getTime !== 'function' || Number.isNaN(date.getTime())) {
    return { ok: false, code: 'PICKUP_INVALID' };
  }
  const ms = date.getTime();
  const nowMs = now.getTime();
  if (ms <= nowMs) return { ok: false, code: 'PICKUP_IN_PAST' };
  const horizon =
    maxPickupMs ?? endOfServiceHorizon(now, { timezone, advanceDays });
  if (ms > horizon) return { ok: false, code: 'PICKUP_TOO_FAR' };

  const grace = Number(gracePeriodMinutes) || 0;

  // An override describes the situation the owner is in *now*. It must not
  // reach into a later service date, which has an ordinary published schedule
  // of its own — otherwise force-opening the restaurant would silently switch
  // off next-day ordering (#485).
  const [todayKey] = serviceDateKeys(now, { timezone, advanceDays: 0 });
  const appliesToday =
    serviceDateKeys(date, { timezone, advanceDays: 0 })[0] === todayKey;

  // Force close (owner, 2026-10-13, #668). Indefinite: nothing can be booked,
  // today or on any later day. With a duration: the close covers only its own
  // window, so a customer may still book a pickup for after it ends and the
  // regular schedule decides from there.
  if (override === 'closed') {
    if (!overrideExpiration || ms < overrideExpiration) {
      return { ok: false, code: 'PICKUP_OUTSIDE_HOURS' };
    }
  }

  // A timed force-open adds a window on top of the published schedule. An
  // indefinite one adds nothing — there is no end to book against — but it no
  // longer takes the published schedule away either (owner, 2026-09-24):
  // force-opening the restaurant should not switch off same-day scheduling
  // during its own normal hours.
  if (override === 'open' && appliesToday && overrideExpiration) {
    const lastBookable = overrideExpiration - grace * 60000;
    if (ms < overrideExpiration && ms <= lastBookable) return { ok: true };
    // Past the override: fall through to the regular schedule.
  }

  const dayHours = hours?.[toRestaurantClock(date, timezone).day];
  if (!Array.isArray(dayHours) || dayHours.length === 0) {
    return { ok: false, code: 'PICKUP_OUTSIDE_HOURS' };
  }

  const { minutes } = toRestaurantClock(date, timezone);
  const inside = dayHours.some(({ start, end }) => {
    const open = hhmmToMinutes(start);
    const close = hhmmToMinutes(end);
    return minutes >= open && minutes <= close - grace && minutes < close;
  });

  return inside ? { ok: true } : { ok: false, code: 'PICKUP_OUTSIDE_HOURS' };
};
